import {
  baseStyles,
  journeyStyles,
  institutionStyles,
  skillsStyles,
  courseStyles,
  cohortStyles,
  questionSetStyles,
  practiceStyles,
} from 'analytics_dashboard/components/custom-styles';
import { useEffect, useState } from 'react';
import t from 'react-translate';
import {
  AnalyticsDashBoardType,
  QrveyConfig,
  QrveyPanel,
  questionSetkeyMapping,
  QrveyState,
  QrveyInstance,
  ChartType,
} from 'redux/schemas/app/analytics';
import { each } from 'underscore';

const setGlobal = (key: string, value: any) => {
  (window as any)[key] = value;
};

const clearGlobal = (key: string) => {
  if ((window as any)[key]) {
    delete (window as any)[key];
  }
};

// Provided by qrvey, this is how we reset the variables so we can switch dashboards and
// provide different CSS every time.
export const resetQrveyValues = () => {
  if (typeof window.customEUStyle !== 'undefined' && window.customEUStyle !== '') {
    window.customEUStyle = undefined;
  }
};

const getOverwrittenStyles = (type: AnalyticsDashBoardType, pageIdKey: string) => `
  ${baseStyles}
  ${type === AnalyticsDashBoardType.LEARNING_JOURNEY ? journeyStyles : ''}
  ${type === AnalyticsDashBoardType.INSTITUTION ? institutionStyles : ''}
  ${(type === AnalyticsDashBoardType.COURSE && pageIdKey === 'skills') ? skillsStyles : ''}
  ${(type === AnalyticsDashBoardType.COURSE && pageIdKey !== 'skills') ? courseStyles : ''}
  ${type === AnalyticsDashBoardType.COHORTS ? cohortStyles : ''}
  ${type === AnalyticsDashBoardType.QUESTION_SET ? questionSetStyles : ''}
  ${type === AnalyticsDashBoardType.COHORT_QUESTION_SET ? questionSetStyles : ''}
  ${type === AnalyticsDashBoardType.PRACTICE_ANALYTICS ? practiceStyles : ''}
`;

const initializeQrvey = (
  qrveydata: QrveyState | QrveyInstance,
  type: AnalyticsDashBoardType,
  pageIdKey: string,
): { config: QrveyConfig; panels?: QrveyPanel[]; title?: string[] } | null => {
  console.log('Initializing Qrvey for type:', type, 'with pageIdKey:', pageIdKey);
  const data: QrveyInstance = type === AnalyticsDashBoardType.COHORT_QUESTION_SET || type === AnalyticsDashBoardType.QUESTION_SET ? qrveydata : qrveydata[type];
  if (!data) return null;
  const {
    token,
    appId,
    userId,
    host,
    pageId,
    pageIds,
    panels: qrveyPanels,
    userFilters,
    topPageId,
    panelsPageId,
    title: questionSetTitle,
    type: questionSetType,
  } = data;

  const config: QrveyConfig = {
    qv_token: token,
    domain: host,
    app_id: appId,
    user_id: userId,
    customCSSRules: getOverwrittenStyles(type, pageIdKey),
    custom_styles: true,
    downloadingBoxText: t.QRVEY.DOWNLOAD_BOX(),
    automaticDownload: false,
    featurePermission: { panels: { global: { hide_CSV: true } } },
  };

  let panels: QrveyPanel[] | undefined;
  let title: string[] | [];

  switch (type) {
    case AnalyticsDashBoardType.COHORTS:
      config.page_id = pageId;
      break;

    case AnalyticsDashBoardType.PRACTICE_ANALYTICS:
      config.page_id = pageId;
      break;

    case AnalyticsDashBoardType.COURSE:
      config.page_id = pageIds[pageIdKey];
      break;

    case AnalyticsDashBoardType.LICENSE:
      config.page_id = pageId;
      config.userFilters = userFilters;
      break;

    case AnalyticsDashBoardType.MENTORSHIP_PROGRAM:
      config.page_id = pageId;
      break;

    case AnalyticsDashBoardType.QUESTION_SET:
    case AnalyticsDashBoardType.COHORT_QUESTION_SET: {
      if (type === AnalyticsDashBoardType.QUESTION_SET) {
        config.dashboardId = topPageId;
      }

      if (qrveyPanels) {
        const questionSetPanels: QrveyPanel[] = [];
        each(qrveyPanels, (panel, index) => {
          let isMetric = false;
          const anPanelConfig: any = {
            domain: host,
            qv_token: token,
            qrveyId: panel.dataSetId,
            user_id: userId,
            app_id: appId,
            userFilters: panel.userFilters,
            panel: {
              header: {
                visible: true,
                filter: false,
                menu: {
                  enable: true,
                  options: [
                    {
                      key: 'download',
                      enable: true,
                      options: [
                        { key: 'jpg', enable: true },
                        { key: 'pdf', enable: true },
                        { key: 'csv', enable: false },
                        { key: 'csv-summary', enable: true },
                        { key: 'excel', enable: true },
                      ],
                    },
                  ],
                },
              },
            },
          };
          if (panel.chartId) {
            anPanelConfig.type = 'CHART';
            anPanelConfig.chart_id = panel.chartId;
          }
          if (panel.metricId) {
            isMetric = true;
            anPanelConfig.type = 'METRIC';
            anPanelConfig.metric_id = panel.metricId;
          }
          setGlobal(`anPanelConfig${index}`, anPanelConfig);

          const questionSetPanel: QrveyPanel = { ...panel };
          if (panel.chartType === ChartType.COMPARISON || panel.chartType === ChartType.ALL || panel.chartType === ChartType.ALL_LEARNERS) {
            if (panel.chartType !== ChartType.ALL_LEARNERS) {
              questionSetPanel.title = panel.chartType === ChartType.COMPARISON
                ? t.QRVEY.COMPARE_RESULTS()
                : t.QRVEY.ALL_QUESTIONS();
            }
            questionSetPanel.className = 'full-panel';
          }
          if (isMetric) {
            questionSetPanel.className = 'number-panel';
          }
          if (panel.chartType === ChartType.LIKERT_SCALE_AVG || panel.chartType === ChartType.LIKERT_SCALE_QUESTION) {
            if (panel.chartType === ChartType.LIKERT_SCALE_AVG) {
              questionSetPanel.likertTitle = panel.title;
            }
            questionSetPanel.title = null;
          }

          questionSetPanels[index] = questionSetPanel;
        });
        panels = questionSetPanels;
      }

      if (questionSetTitle) {
        title = [t.COURSE_ANALYTICS[questionSetkeyMapping[questionSetType]](), questionSetTitle];
      }
      break;
    }

    default:
      break;
  }

  return { config, panels, title };
};

export const useQrveyInitializer = (
  data: QrveyState,
  isReady: boolean,
  isLoading: boolean,
  keyName: string,
  pageIdKey: string,
  type: AnalyticsDashBoardType,
) => {
  const [pageTitle, setPageTitle] = useState<string[] | undefined>([]);
  const [panels, setPanels] = useState<QrveyPanel[]>();
  const [isError, setIsError] = useState<boolean>(false);

  const configKeyName = keyName || 'config';

  useEffect(() => {
    if (data && isReady && !isLoading) {
      const qrveyConfig = initializeQrvey(data, type, pageIdKey);
      if (!qrveyConfig) {
        setIsError(true);
        return;
      }
      const { config, panels: qrveyPanels, title } = qrveyConfig;

      // Assign config to global window object
      clearGlobal(configKeyName);
      setGlobal(configKeyName, config);

      setPanels(qrveyPanels);
      setPageTitle(title);
    }
  }, [data, isReady, isLoading, type, pageIdKey, configKeyName]);

  useEffect(() => () => {
    // Cleanup when component unmounts
    console.log(`Cleaning up existing config: ${configKeyName}`);
    clearGlobal(configKeyName);
    resetQrveyValues();
  }, [configKeyName, type]);

  return { panels, pageTitle, isError };
};

